<template>
    <div class="mt-5" v-if="isTeacherMode">
        <div class="flex">
            <span>{{ $t('messages.filter') }}</span>
            <SelectAllNoneButtons
                :max-count="scoredStudents.length"
                :selected-count="filteredStudents.length"
                @select-all="selectAllStudents()"
                @select-none="deselectAllStudents()"
                class="flex"
            />
        </div>
        <AppWell>
            <AppCard>
                <div class="flex -space-x-1 flex-wrap relative z-0">
                    <label v-for="student in scoredStudents" :key="student.id">
                        <input type="checkbox" v-model="student.is_selected" class="hidden" />

                        <img
                            :title="student.full_name"
                            :src="student.avatar_or_default"
                            class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                            :class="{ 'opacity-50': !student.is_selected }"
                            alt=""
                        />
                    </label>
                </div>
            </AppCard>
        </AppWell>
    </div>

    <div class="md:grid md:grid-cols-6 md:gap-6" v-if="isTeacherMode">
        <div class="sm:col-span-1">
            <label class="block text-sm font-medium leading-5 text-gray-700">
                {{ $t('messages.display_as') }}
            </label>
            <div class="mt-1">
                <div
                    class="inline-flex items-center"
                    v-for="displayOption in displayOptions"
                    :key="`display_option_${displayOption.value}`"
                >
                    <input
                        name="display_option"
                        :id="`display_option_${displayOption.value}`"
                        :value="displayOption.value"
                        v-model="show"
                        type="radio"
                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                    />
                    <label class="ml-1 mr-3" :for="`display_option_${displayOption.value}`">
                        <span class="block text-sm leading-5 font-medium text-gray-700">
                            {{ displayOption.text }}
                        </span>
                    </label>
                </div>
            </div>
        </div>
    </div>

    <div class="flex-col mt-5">
        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div
                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
            >
                <table class="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.student') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                v-if="activity.max_score !== 10"
                            >
                                {{ $t('messages.correct_answers') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                v-if="activity.max_score !== 10"
                            >
                                {{ $tc('messages.grades', 1) }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                v-if="activity.max_score !== 10"
                            >
                                {{ $t('messages.date') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                v-if="isTeacherMode"
                            ></th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                v-if="activity.max_score !== 10 && isTeacherMode"
                            ></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(student, index) in students"
                            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                            class="hover:bg-gray-200"
                            :key="student.id"
                        >
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                {{ student.full_name }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                <span v-if="student.done">
                                    <Tippy>
                                        <span class="px-2 py-1">
                                            {{ student.correct_answers }} / {{ student.answers_count }}
                                        </span>
                                        <template #content>
                                            <ul>
                                                <li v-if="student.correct_answers_count !== null">
                                                    {{ $t('messages.correct_answers_count') }}:
                                                    {{ student.correct_answers_count }}
                                                </li>
                                                <li v-if="student.incorrect_answers_count !== null">
                                                    {{ $t('messages.incorrect_answers_count') }}:
                                                    {{ student.incorrect_answers_count }}
                                                </li>
                                                <li v-if="student.unanswered_answers_count !== null">
                                                    {{ $t('messages.unanswered_answers_count') }}:
                                                    {{ student.unanswered_answers_count }}
                                                </li>
                                            </ul>
                                        </template>
                                    </Tippy>
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                v-if="show === 'score'"
                            >
                                {{ student.decimal_score }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                :class="student.colors"
                                v-else
                            >
                                {{ student.letter_grade }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                <div>
                                    {{ student.duration_display }}
                                </div>
                                <div class="flex space-x-2 items-baseline">
                                    <div class="block md:inline-block">
                                        {{ student.diff_for_humans }}
                                    </div>
                                    <span class="text-xs text-gray-600">
                                        {{ student.date_time }}
                                    </span>
                                </div>
                                <div class="text-xs text-gray-600 flex gap-2" v-if="student.reviewed_at">
                                    <div class="inline-block">
                                        <Tippy>
                                            <IconEyeExclamation class="text-yellow-600 h-4 w-4" />
                                            <template #content>
                                                {{ student.review_info.review_result_message }}
                                            </template>
                                        </Tippy>
                                    </div>

                                    <span>
                                        {{ $t('messages.reviewed_at_X', { time: student.reviewed_at }) }}
                                    </span>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                v-if="isTeacherMode"
                            >
                                {{ student.adaptation_degree_display }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                v-if="isTeacherMode"
                            >
                                <InertiaSquareConfirmActionButton
                                    :href="student.links.attempt_delete"
                                    danger
                                    :confirm-text="$t('messages.delete')"
                                    method="delete"
                                    v-if="student.links.attempt_delete"
                                >
                                    <template v-slot:icon>
                                        <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                            <path
                                                fill-rule="evenodd"
                                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </template>
                                    {{ $t('messages.are_you_sure?') }}
                                    <br />
                                    {{ student.delete_attempt_message }}
                                </InertiaSquareConfirmActionButton>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <div class="mt-5">
        <div v-for="question in questions" :key="question.question_id" :id="`question_${question.question_id}`">
            <QuestionReviewDisplay :students="filteredStudents" :is-teacher-mode="isTeacherMode" :question="question" />
        </div>
    </div>
</template>

<script>
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import { Tippy } from 'vue-tippy';
import QuestionReviewDisplay from '@/components/QuestionReviewDisplay.vue';
import { IconEyeExclamation, IconHelpCircle } from '@tabler/icons-vue';

export default {
    components: {
        IconHelpCircle,
        IconEyeExclamation,
        QuestionReviewDisplay,
        Tippy,
        InertiaSquareConfirmActionButton,
        AppWell,
        AppCard,
        SelectAllNoneButtons,
    },
    name: 'QuizReview',
    props: {
        questions: {
            required: true,
            type: Array,
        },
        activity: {
            required: true,
            type: Object,
        },
        students: {
            required: true,
            type: Array,
        },
        mode: {
            required: true,
            type: String,
        },
    },
    data() {
        return {
            show: 'letter',
            displayOptions: [
                { text: this.$tc('messages.scores', 2), value: 'score' },
                { text: this.$tc('messages.letters', 2), value: 'letter' },
            ],
            scoredStudents: this.students.filter((s) => s.done),
        };
    },
    methods: {
        selectAllStudents() {
            this.scoredStudents.forEach((s) => (s.is_selected = true));
        },
        deselectAllStudents() {
            this.scoredStudents.forEach((s) => (s.is_selected = false));
        },
    },
    computed: {
        filteredStudents: function () {
            return this.scoredStudents.filter((s) => s.is_selected);
        },
        isTeacherMode: function () {
            return this.mode === 'teacher';
        },
    },
};
</script>
